import {
  DeepReadonly,
  inject,
  provide,
  reactive,
  readonly,
  toRefs,
  type ToRefs,
  type InjectionKey,
} from 'vue'

type AnyFn = (..._a: any[]) => any

export type LocalStoreActions<State extends object> = Record<
  string,
  ((_state: State) => any) | ((_state: State, _payload: any) => any)
>

type RemoveStateArg<A, S> = {
  [K in keyof A]: A[K] extends (_state: S, _payload: infer P) => infer R
    ? undefined extends P
      ? (_payload?: P) => R
      : (_payload: P) => R
    : A[K] extends (_state: S) => infer R
    ? () => R
    : never
}

type LocalStoreOptions<
  State extends object,
  Actions extends LocalStoreActions<State>,
  P = undefined
> = {
  state: (_payload: P) => State
  actions?: Actions
}

type LocalStoreSetup<T, P = undefined> = (_payload: P) => T

type StoreFromOptions<
  S extends object,
  A extends LocalStoreActions<S>
> = ToRefs<DeepReadonly<S>> & RemoveStateArg<A, S>

export function defineLocalStore<
  State extends object,
  Actions extends LocalStoreActions<State>,
  P = undefined
>(
  _storeName: string,
  _options: LocalStoreOptions<State, Actions, P>
): {
  initLocalStore: undefined extends P
    ? (_payload?: P) => StoreFromOptions<State, Actions>
    : (_payload: P) => StoreFromOptions<State, Actions>
  useLocalStore: () => StoreFromOptions<State, Actions>
}

export function defineLocalStore<T extends object, P = undefined>(
  _storeName: string,
  _setupFn: LocalStoreSetup<T, P>
): {
  initLocalStore: undefined extends P ? (_payload?: P) => T : (_payload: P) => T
  useLocalStore: () => T
}

export function defineLocalStore(storeName: string, arg: any) {
  const storeKey: InjectionKey<any> = Symbol(`localStore:${storeName}`)

  const isStoreSetup = (x: any): x is LocalStoreSetup<any, any> =>
    typeof x === 'function'
  const isStoreOptions = (x: any): x is LocalStoreOptions<any, any, any> =>
    typeof x === 'object' && x !== null && 'state' in x

  function initLocalStore(payload?: unknown) {
    let store: any

    if (isStoreSetup(arg)) {
      store = arg(payload as never)
    } else if (isStoreOptions(arg)) {
      store = createStoreOptions(arg, payload as never)
    }

    if (!store) {
      throw new Error(`[localStore] Invalid store definition: ${storeName}`)
    }

    provide(storeKey, store)
    return store
  }

  function useLocalStore() {
    const store = inject(storeKey)
    if (!store) {
      throw new Error(`[localStore] Store "${storeName}" is not provided`)
    }
    return store
  }

  return { initLocalStore, useLocalStore }
}

function createStoreOptions<
  State extends object,
  Actions extends LocalStoreActions<State>,
  P
>(options: LocalStoreOptions<State, Actions, P>, payload: P) {
  const state = reactive(options.state(payload))
  const actions = {} as RemoveStateArg<Actions, State>

  if (options.actions) {
    for (const key of Object.keys(options.actions) as Array<keyof Actions>) {
      const fn = options.actions[key]!
      // Обёртка, устраняющая первый аргумент (state)
      actions[key] = ((p?: unknown) =>
        (fn as AnyFn)(state, p)) as unknown as RemoveStateArg<
        Actions,
        State
      >[typeof key]
    }
  }

  return {
    ...(toRefs(readonly(state)) as ToRefs<DeepReadonly<State>>),
    ...actions,
  }
}
